// src/components/AccountLinkingPage.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
} from '@mui/material';
import { Sync as SyncIcon, Link as LinkIcon, LinkOff as LinkOffIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiFetch from '../utils/api';

function AccountLinkingPage() {
  const { user, blestaLinked, blestaServices, refreshBlestaStatus } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [syncSuccess, setSyncSuccess] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    setSyncError(null);
    setSyncSuccess(false);

    try {
      const response = await apiFetch('/api/blesta/sync', {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        setSyncSuccess(true);
        setTimeout(() => setSyncSuccess(false), 5000);
        if (refreshBlestaStatus) {
          await refreshBlestaStatus();
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to sync services');
      }
    } catch (err) {
      console.error('Error syncing services:', err);
      setSyncError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const getServiceStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 0.5 }}>
          Account
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Manage your Discord identity and linked services.
        </Typography>
      </Box>

      {/* Discord Identity Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Discord Account
          </Typography>
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Avatar
                alt={user.username}
                src={user.avatar}
                sx={{ width: 48, height: 48 }}
              />
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {user.username}
                </Typography>
                {user.discordId && (
                  <Typography variant="body2" color="textSecondary" fontFamily="monospace">
                    ID: {user.discordId}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Blesta Link Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Blesta Account Link
            </Typography>
            {blestaLinked === null ? (
              <CircularProgress size={20} />
            ) : blestaLinked ? (
              <Chip icon={<LinkIcon />} label="Linked" color="success" />
            ) : (
              <Chip icon={<LinkOffIcon />} label="Not Linked" color="default" />
            )}
          </Box>

          {blestaLinked === null && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {blestaLinked === false && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Link your Discord account in the Blesta client portal to access your bots.
              Visit{' '}
              <a
                href="https://portal.highland-networks.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                portal.highland-networks.com
              </a>{' '}
              and link your Discord account from your profile settings.
            </Alert>
          )}

          {blestaLinked === true && (
            <>
              {syncError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSyncError(null)}>
                  {syncError}
                </Alert>
              )}

              {syncSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Services synced successfully!
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={syncing ? <CircularProgress size={18} /> : <SyncIcon />}
                  onClick={handleSync}
                  disabled={syncing}
                >
                  Sync Services
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Services
              </Typography>

              {blestaServices && blestaServices.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Namespace</TableCell>
                        <TableCell>Bot Slots</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {blestaServices.map((service, index) => (
                        <TableRow key={service.serviceId || index}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {service.namespace}
                            </Typography>
                          </TableCell>
                          <TableCell>{service.botSlots}</TableCell>
                          <TableCell>
                            <Chip
                              label={service.status}
                              color={getServiceStatusColor(service.status)}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No services found. If you recently linked your account, try syncing.
                </Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default AccountLinkingPage;
